import { Link, useParams } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import NotFoundPage from '../NotFoundPage';
import { comparisons } from '../compare/registry';
import { findPost, postsTagged } from './postLookup';
import { getTopic, topics } from './topics';
import type { TopicLink } from './topics';

const SITE_URL = 'https://www.greyfielddata.com';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

function ReadingLink({ link }: { link: TopicLink }) {
  const post = link.slug ? findPost(link.slug) : undefined;
  const href = link.slug ? `/blog/${link.slug}` : link.href;
  const label = post?.title || link.label;
  // A curated post that is scheduled but not yet published is hidden until its date.
  if (link.slug && !post) return null;
  if (!href || !label) return null;
  return (
    <li>
      <Link to={href} className="group flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-blue-200 hover:shadow-md sm:p-5">
        {post?.cover && (
          <img src={post.cover} alt="" width="1200" height="630" loading="lazy" decoding="async" className="hidden aspect-[1200/630] w-32 shrink-0 rounded-lg bg-[#071525] object-cover sm:block" />
        )}
        <span className="min-w-0">
          <span className="block font-semibold leading-snug text-slate-950 group-hover:text-[#1a4e94]">{label}</span>
          <span className="mt-1.5 block text-sm leading-6 text-slate-600">{link.note}</span>
        </span>
      </Link>
    </li>
  );
}

export default function TopicHub() {
  const { topic: slug } = useParams<{ topic: string }>();
  const topic = slug ? getTopic(slug) : undefined;
  if (!topic) return <NotFoundPage />;

  const url = `${SITE_URL}/resources/${topic.slug}`;
  const curated = new Set(topic.sections.flatMap((section) => section.links.map((link) => link.slug)).filter(Boolean));
  const more = postsTagged(topic.tag).filter((post) => !curated.has(post.slug));
  const relatedComparisons = topic.compareGroup ? comparisons.filter((page) => page.group === topic.compareGroup) : [];
  const otherTopics = topics.filter((candidate) => candidate.slug !== topic.slug);

  return (
    <BlogLayout wide>
      <SEO title={topic.seoTitle} description={topic.description} url={url} image={`${SITE_URL}${topic.art}`} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: topic.name, url },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">{topic.name}</span>
      </nav>

      <header className="mt-5 grid items-center gap-8 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">{topic.title}</h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-600">{topic.intro}</p>
          <nav aria-label="On this page" className="mt-6 flex flex-wrap gap-2">
            {topic.sections.map((section, index) => (
              <a key={section.heading} href={`#section-${index + 1}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-blue-200 hover:text-[#1a4e94]">
                {section.heading}
              </a>
            ))}
          </nav>
        </div>
        <img src={topic.art} alt="" width="1440" height="480" decoding="async" className="aspect-[3/1] w-full rounded-2xl bg-slate-950 object-cover" />
      </header>

      <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-12">
          {topic.sections.map((section, index) => (
            <section key={section.heading} id={`section-${index + 1}`} className="scroll-mt-28">
              <p className="text-sm font-semibold tabular-nums text-[#1a4e94]">{String(index + 1).padStart(2, '0')}</p>
              <h2 className="mt-1 text-2xl font-semibold text-slate-950">{section.heading}</h2>
              <p className="mt-2 text-slate-600">{section.intro}</p>
              <ul className="mt-5 grid gap-3">
                {section.links.map((link) => <ReadingLink key={link.slug || link.href} link={link} />)}
              </ul>
            </section>
          ))}

          {more.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-slate-950">More {topic.name} articles</h2>
              <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {more.map((post) => (
                  <li key={post.slug}><Link to={`/blog/${post.slug}`} className="text-[#1a4e94] underline underline-offset-4 hover:text-slate-950">{post.title}</Link></li>
                ))}
              </ul>
            </section>
          )}

          {relatedComparisons.length > 0 && (
            <section>
              <h2 className="text-2xl font-semibold text-slate-950">Comparisons</h2>
              <p className="mt-2 text-slate-600">Where another tool is the better fit, these pages say so.</p>
              <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {relatedComparisons.map((page) => (
                  <li key={page.slug}><Link to={`/compare/${page.slug}`} className="text-[#1a4e94] underline underline-offset-4 hover:text-slate-950">{page.title}</Link></li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h2 className="text-2xl font-semibold text-slate-950">Common questions</h2>
            <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
              {topic.faqs.map((faq) => (
                <div key={faq.question} className="py-5">
                  <h3 className="font-semibold text-slate-950">{faq.question}</h3>
                  <p className="mt-2 leading-7 text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Tools</h2>
            <ul className="mt-4 space-y-4">
              {topic.tools.map((tool) => (
                <li key={tool.href}>
                  {(tool.href.endsWith('.md') || tool.href.endsWith('.csv') || tool.href.endsWith('.pdf')) ? (
                    <a href={tool.href} download className="font-semibold text-[#1a4e94] hover:text-slate-950">{tool.label}</a>
                  ) : (
                    <Link to={tool.href} className="font-semibold text-[#1a4e94] hover:text-slate-950">{tool.label}</Link>
                  )}
                  <p className="mt-1 text-sm leading-6 text-slate-600">{tool.note}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-slate-950 p-6 text-white">
            <h2 className="text-lg font-semibold">Working on this now?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">Bring the report, query, or setup you are stuck on. We can look at it together.</p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-sky-100">Discuss your data problem</a>
            <ul className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              {topic.services.map((service) => (
                <li key={service.href}><Link to={service.href} className="text-blue-200 underline underline-offset-4 hover:text-white">{service.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="px-1">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Other topics</h2>
            <ul className="mt-3 space-y-2">
              {otherTopics.map((candidate) => (
                <li key={candidate.slug}><Link to={`/resources/${candidate.slug}`} className="font-medium text-[#1a4e94] hover:text-slate-950">{candidate.title}</Link></li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </BlogLayout>
  );
}
